import { BundleAdditionalFragment, formatPrice } from "@stepapp/core";
import type { OrderServicePayload, ReplacementCheckoutInput } from "@stepapp/core/apiTypes";
import { Block, Button, Divider, Loader, Typography, spacing } from "@stepapp/ui";
import { useCallback } from "react";
import type { UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { StyleSheet } from "react-native";
import QuantityActions from "../../components/Service/QuantityActions";
import QuantityAnswerItem from "../../components/Service/QuantityAnswerItem";
import { AppStorage } from "../../services/AppStorage";

type EditServicesProps = {
  question: BundleAdditionalFragment;
  notIncludedServices?: BundleAdditionalFragment[];
  services: OrderServicePayload[];
  questionId?: string;
  isOneTimeService: boolean;
  setShowDeleteModal: (v: boolean) => void;
  setValue: UseFormSetValue<ReplacementCheckoutInput>;
  loading: boolean;
  serviceToDeleteName?: string;
  additionalServiceTitle: string;
  showCustomAdditionalDetails: boolean;
};

const EditServices = ({
  question,
  services,
  questionId,
  setValue,
  setShowDeleteModal,
  isOneTimeService,
  notIncludedServices,
  loading,
  serviceToDeleteName,
  additionalServiceTitle,
  showCustomAdditionalDetails,
}: EditServicesProps) => {
  const { t } = useTranslation();
  const priceFormatter = formatPrice(AppStorage().get("countryCode"));

  const setNewServices = useCallback(
    (services: OrderServicePayload[]) => {
      setValue("services", services, { shouldDirty: true, shouldTouch: true });
    },
    [setValue],
  );

  const serviceTitle = showCustomAdditionalDetails
    ? additionalServiceTitle
    : t("screens.service.additionalServices");

  const handleQuantityChange = useCallback(
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TODO: refactor
    (serviceId: string, variantId: string, quantity: number, singleVariant: boolean) => {
      const currentQuestionId = questionId || serviceId;

      const oldServices = services?.filter((s) => s?.serviceId !== currentQuestionId) || [];
      const serviceToUpdate = services?.find((s) => s?.serviceId === currentQuestionId);

      if (singleVariant || !serviceToUpdate) {
        if (quantity === 0) {
          setNewServices(oldServices);
          return;
        }

        setNewServices(oldServices.concat([{ serviceId, variants: [{ quantity, variantId }] }]));
      } else {
        const variantToUpdate = serviceToUpdate?.variants.find((v) => v?.variantId === variantId);

        if (variantToUpdate) {
          const oldVariants = serviceToUpdate?.variants.filter((v) => v?.variantId !== variantId);

          if (quantity === 0 && oldVariants.length === 0) {
            setNewServices(oldServices);
            return;
          }

          const newVariants =
            quantity === 0
              ? oldVariants
              : serviceToUpdate?.variants.map((variant) =>
                  variant?.variantId === variantId ? { ...variant, quantity } : variant,
                );

          const newServices = services?.map((service) =>
            service?.serviceId === currentQuestionId
              ? { ...service, variants: newVariants }
              : service,
          );

          setNewServices(newServices || []);
        } else {
          const newServices = services?.map((service) =>
            service?.serviceId === currentQuestionId
              ? { ...service, variants: service?.variants.concat([{ quantity, variantId }]) }
              : service,
          );

          setNewServices(newServices || []);
        }
      }
    },
    [questionId, services, setNewServices],
  );

  if (loading) {
    return (
      <Block alignCenter justifyCenter height={16}>
        <Loader />
      </Block>
    );
  }

  if (!questionId) {
    return (
      <Block marginVertical={3} marginHorizontal={2.5}>
        <Block marginBottom={2}>
          <Typography variant="newH3">{serviceTitle}</Typography>
        </Block>

        {notIncludedServices?.map(({ icon, id, name, description, variants }) => (
          <QuantityAnswerItem
            description={description}
            icon={icon}
            id={id}
            key={id}
            name={name}
            variants={variants}
            setNewServices={setNewServices}
            services={services}
          />
        ))}
      </Block>
    );
  }

  return (
    <>
      <Block marginVertical={3} marginHorizontal={2.5} flex={1}>
        <Typography variant="newH3">
          {question ? question?.name : t("screens.serviceDetails.cantUpdateAdditionalService")}
        </Typography>
        <Block marginBottom={3} marginTop={1}>
          <Typography type="secondary" variant="lightLabel">
            {question?.description}
          </Typography>
        </Block>
        {question?.variants?.map(({ description, price, id, title }) => {
          const selectedService = services
            ?.flatMap((obj) => obj?.variants)
            .find((variant) => variant?.variantId === id);
          const singleVariant = question?.variants.length === 1;
          return (
            <Block key={id}>
              <Block alignCenter row>
                <Block flex={1}>
                  <Typography variant="bodyBold">{`${title} ${
                    description && `(${description})`
                  }`}</Typography>
                  <Block marginTop={0.5}>
                    <Typography type="secondary" variant="paragraph">
                      {priceFormatter(price || 0)}
                    </Typography>
                  </Block>
                </Block>

                <QuantityActions
                  inModal
                  onChange={(quantity) => {
                    handleQuantityChange(questionId, id, quantity, singleVariant);
                  }}
                  quantity={selectedService?.quantity || 0}
                />
              </Block>

              {!isOneTimeService && <Divider style={styles.divider} />}
            </Block>
          );
        })}

        {!isOneTimeService && (
          <Block marginTop={2}>
            <Button
              label={t("common.deleteService", { service: question?.name || serviceToDeleteName })}
              onPress={() => setShowDeleteModal(true)}
              type="errorLink"
            />
          </Block>
        )}
      </Block>
    </>
  );
};

export default EditServices;

const styles = StyleSheet.create({
  divider: {
    marginTop: spacing.unit * 2,
  },
});
